import { useCallback, useMemo, useState } from 'react';
import classnames from 'clsx';
import { FormattedMessage } from 'react-intl';
import Select from 'react-select';

import {
  DropdownIndicator,
  ClearIndicator,
  IndicatorSeparator,
  GroupHeading,
} from 'ui-components/components/dropdown/react-select-wrapper/internal-components/';
import { RequiredIndicator } from 'ui-components/components/required-indicator';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';

import { DROPDOWN_WRAPPER_CONTAINER } from '../../../../constants/element-ids';
import { messages } from '../messages';
import style from './react-select-dropdown.module.less';

/**
 * @typedef {import('../dropdown-types.d.ts').DropdownOption} DropdownOption
 */
/**
 * @typedef {import('./dropdown-types.d.ts').DropdownOptionGroup} DropdownOptionGroup
 */
/**
 * @typedef {import('./dropdown-types.d.ts').DropdownOnChange} DropdownOnChange
 */

// According to react select docs, these should be defined "outside the scope of rendering the Select"
// https://react-select.com/components#defining-components
const componentOverrides = {
  ClearIndicator,
  DropdownIndicator,
  GroupHeading,
  IndicatorSeparator,
};

const staticClassNames = {
  clearIndicator: () => style['clear-indicator'],
  group: () => style.group,
  input: () => style.input,
  indicatorsContainer: () => style['indicators-container'],
  indicatorSeparator: () => style['indicator-separator'],
  menu: () => style.menu,
  menuList: () => style['menu-list'],
  option: (state) =>
    classnames(style.option, {
      [style['is-disabled']]: state.isDisabled,
      [style['is-focused']]: state.isFocused,
      [style['is-selected']]: state.isSelected,
    }),
  singleValue: () => style['single-value'],
  valueContainer: () => style['value-container'],
};

const defaultGetOptionIsDisabled = (option) => option.disabled;
const defaultGetOptionLabel = (option) => option.label;
const defaultGetOptionValue = (option) => option.value;

const getNoOptionsMessage = () => <FormattedMessage {...messages.noOptions} />;

/**
 * @param {object} props
 * @param {string} [props.ariaLabel]
 * @param {string} [props.className]
 * @param {boolean} [props.isMenuFullWidth]
 * @param {(option: DropdownOption) => boolean} [props.getOptionIsDisabled] - Defaults to `option.disabled`
 * @param {(option: DropdownOption, selectedOptions: DropdownOption[]) => boolean} [props.getOptionIsSelected] - This is typically not necessary unless there are multiple values used to determine if an option is selected. This should be used in conjunction with `selectedOption` instead of `selectedItemValue`
 * @param {(option: DropdownOption) => string} [props.getOptionLabel] - Defaults to `option.label`
 * @param {(option: DropdownOption) => string} [props.getOptionValue] - Defaults to `option.value`
 * @param {boolean} [props.hasError]
 * @param {string} [props.inputId]
 * @param {boolean} [props.isClearable]
 * @param {boolean} [props.isDisabled]
 * @param {boolean} [props.isSearchable]
 * @param {boolean} [props.required]
 * @param {DropdownOnChange} props.onItemSelected
 * @param {DropdownOptionGroup[]} [props.optionGroups]
 * @param {DropdownOption[]} [props.options]
 * @param {string} [props.placeholder]
 * @param {(option: DropdownOption) => ReactNode} [props.renderOption]
 * @param {(option: DropdownOption) => ReactNode} [props.renderSelectedValue]
 * @param {string} props.selectedItemValue
 * @param {DropdownOption} [props.selectedOption] - `selectedItemValue` should typically be preferred over this. `selectedOption` is only necessary when the selected option is not in the list of options or when multiple properties are needed to determine if an option is selected
 * @param {boolean} [props.shouldTruncateGroupHeader]
 * @returns {JSX.Element}
 */
export const ReactSelectDropdown = ({
  ariaLabel,
  className,
  isMenuFullWidth,
  getOptionIsDisabled = defaultGetOptionIsDisabled,
  getOptionIsSelected,
  getOptionLabel = defaultGetOptionLabel,
  getOptionValue = defaultGetOptionValue,
  hasError,
  inputId,
  isClearable = false,
  isDisabled,
  isSearchable = false,
  onItemSelected,
  optionGroups,
  options,
  placeholder,
  required = false,
  renderOption,
  renderSelectedValue,
  selectedItemValue = '',
  selectedOption,
  shouldTruncateGroupHeader = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const _selectedOption = useMemo(() => {
    if (selectedOption) {
      return selectedOption;
    }

    const allOptions = optionGroups ? optionGroups.flatMap((optionGroup) => optionGroup.options) : options;

    // please don't remove this "?? null" part - as a workaround for not clearing Select if value not found
    // we need to return null instead of undefined
    return allOptions.find((option) => getOptionValue(option) === selectedItemValue) ?? null;
  }, [selectedOption, optionGroups, options, getOptionValue, selectedItemValue]);

  const onChange = useCallback(
    (newSelectedOption, meta) => {
      const newOptionValue = newSelectedOption ? getOptionValue(newSelectedOption) : null;

      onItemSelected(newOptionValue, newSelectedOption);

      // Close the dropdown if the user clears the value
      if (meta.action === 'clear') {
        setIsOpen(false);
      }
    },
    [getOptionValue, onItemSelected],
  );

  const zIndex = useMaxZ();
  const styles = useMemo(() => {
    return {
      menuPortal: (base) => ({
        ...base,
        zIndex,
      }),
    };
  }, [zIndex]);

  const classNames = useMemo(() => {
    return {
      ...staticClassNames,
      control: ({ menuIsOpen, isFocused }) =>
        classnames(
          style['base-combobox'],
          { [style['is-focused']]: isFocused },
          { [style['has-error']]: hasError },
          { [style['is-open']]: menuIsOpen },
          className,
        ),
      menu: () => classnames(style.menu, { [style['menu-full-width']]: isMenuFullWidth }),
      groupHeading: () =>
        classnames(style['group-heading'], {
          [style.truncate]: shouldTruncateGroupHeader,
        }),
    };
  }, [className, isMenuFullWidth, hasError, shouldTruncateGroupHeader]);

  const filterOption = useCallback(
    (optionToFilter, inputValue) => {
      const query = inputValue?.trim();
      if (!query) {
        return true;
      }

      const optionLabel = getOptionLabel(optionToFilter.data);
      const lowerCaseOptionText = optionLabel.toLowerCase() || '';
      const lowerCaseInputText = query.toLowerCase();
      return lowerCaseOptionText.includes(lowerCaseInputText);
    },
    [getOptionLabel],
  );

  const formatOptionLabel = useCallback(
    (option, meta) => {
      // According to design, we should typically show the same content in the menu and the selected value, so default to
      // using `renderOption` for both, unless `renderSelectedValue` is explicitly provided.

      if (meta?.context === 'value' && renderSelectedValue) {
        return renderSelectedValue(option);
      }

      if (renderOption) {
        return renderOption(option);
      }

      return getOptionLabel(option);
    },
    [getOptionLabel, renderOption, renderSelectedValue],
  );

  const _filterOption = isSearchable ? filterOption : undefined;

  const onMenuClose = useCallback(() => setIsOpen(false), []);
  const onMenuOpen = useCallback(() => setIsOpen(true), []);
  const menuPortalTarget = global.document.getElementById(DROPDOWN_WRAPPER_CONTAINER);

  const SelectComponent = (
    <Select
      aria-label={ariaLabel}
      backspaceRemovesValue={false}
      classNames={classNames}
      components={componentOverrides}
      escapeClearsValue
      filterOption={_filterOption}
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      inputId={inputId}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isOptionDisabled={getOptionIsDisabled}
      isOptionSelected={getOptionIsSelected}
      isSearchable={isSearchable}
      menuIsOpen={isOpen}
      menuPlacement='auto'
      menuPortalTarget={menuPortalTarget}
      noOptionsMessage={getNoOptionsMessage}
      onChange={onChange}
      onMenuClose={onMenuClose}
      onMenuOpen={onMenuOpen}
      options={optionGroups || options}
      placeholder={placeholder}
      styles={styles}
      value={_selectedOption}
    />
  );

  return required ? (
    <div className={style['select-required-wrapper']}>
      {SelectComponent}
      <RequiredIndicator className={style['required-indicator']} />
    </div>
  ) : (
    SelectComponent
  );
};
